// §2.9 customer price overrides — the pure half of the Pricing tab's panel
// (Module 09, slice P3-C-2, D-263). Unit-tested; the component is the thin
// layer over these.
//
// THE TWO ABSENCES ARE DIFFERENT FACTS. A row's `unit_price_minor` and
// `discount_pct` are each NULL for the kind that does not carry them (the §2.9
// kind-pairing CHECK) and OMITTED — both of them — for a reader without
// `sales.read_value` (§7, D-225: omission, never null, never a marker). So
// `null` is "this kind has no such number" and a missing key is "you may not
// see it" — and the screen renders the second as a SENTENCE ("not shown to
// your role"), never as a blank cell that reads as "nothing was negotiated".
//
// A PROPOSAL'S OUTCOME IS READ OFF THE RESPONSE, never re-derived. The guard
// runs server-side at submit (§3.4, §7 "auto-routes to approval on breach")
// and answers `active` or `pending_approval` with `review` evidence that is
// itself §7-redacted by omission: the approver tier is always there, the
// figures only for the roles that may read them. `proposalOutcome` folds that
// into the one sentence the dialog's caller says.

import type { ChipVariant } from "../../../../components/status-chip";
import { RequestError } from "../../../../lib/request";

export const OVERRIDE_STATUSES = ["pending_approval", "active", "expired", "revoked"] as const;
export type OverrideStatus = (typeof OVERRIDE_STATUSES)[number];

/**
 * The states the list FILTER offers — the wire enum minus `expired`. §5 /
 * D-263: `status = 'expired'` is WRITTEN by no job in P3 (the column stays
 * `active` until module 11/12's scheduler owns the transition in P4), so a
 * `?status=expired` read answers an empty page for every customer — an
 * always-empty filter is the disabled shell D-088 forbids. The expired rows
 * are found under `active` (and under "all"), labelled by
 * `effectiveOverrideStatus` below.
 */
export const FILTERABLE_OVERRIDE_STATUSES = ["pending_approval", "active", "revoked"] as const;

/** §5 revoke's "window has closed": `valid_to < today`. An open-ended row
 * (`valid_to` NULL) never closes; a row ending TODAY is still inside its
 * window (the §3.2 validity filter is inclusive). ISO dates compare as text. */
export function overrideWindowClosed(validTo: string | null | undefined, today: string): boolean {
  return validTo !== null && validTo !== undefined && validTo.length > 0 && validTo < today;
}

/**
 * The state the glass says — DERIVED (D-263): an `active` row whose window has
 * closed is `expired`, whatever the column holds, because the §3.2 engine
 * stopped applying it the day after `valid_to` and a chip reading "Active"
 * over a price nothing applies is a lie. Every other state is the wire's:
 * `pending_approval` stays a proposal until it is decided (a closed window
 * does not decide it), `revoked` is a stronger fact than any window.
 */
export function effectiveOverrideStatus(
  row: { readonly status: string; readonly valid_to: string | null },
  today: string,
): string {
  if (row.status === "active" && overrideWindowClosed(row.valid_to, today)) return "expired";
  return row.status;
}

export const OVERRIDE_KINDS = ["fixed_price", "discount_pct"] as const;
export type OverrideKind = (typeof OVERRIDE_KINDS)[number];

/** The row's chip: a live price is OK, a proposal waiting on a decision is
 * attention, an expired one is a fact that already happened, a revoked one is
 * loud because somebody withdrew a price a customer may have been told. */
export function overrideStatusVariant(status: string): ChipVariant {
  switch (status) {
    case "active":
      return "ok";
    case "pending_approval":
      return "warn";
    case "revoked":
      return "bad";
    default:
      return "neutral";
  }
}

/** §5 revoke is legal FROM `active` and `pending_approval` (an admin
 * withdrawing a proposal) while the window is open; `expired`/`revoked` AND
 * any row whose window has closed (`valid_to < today`, D-263) answer 409
 * OVERRIDE_TERMINAL, so the control is not offered on them (D-088).
 *
 * WHICH CALENDAR, STATED RATHER THAN ASSUMED: `today` here is the VIEWER's
 * (D-089, the quote book's precedent), while the server judges the same
 * window in the TENANT's. For the hours the two disagree — a viewer east of
 * Almaty on the row's last day — this hides a Revoke the server would still
 * honour, or offers one it will refuse. Hiding is harmless; the offer is
 * backstopped by the 409, which the panel renders as the server's own
 * refusal rather than a dead press (pinned by the kernel test). It is not
 * closed for real until this screen has the tenant's timezone in hand, which
 * no sales surface carries today. */
export function overrideIsRevocable(
  row: { readonly status: string; readonly valid_to: string | null },
  today: string,
): boolean {
  if (overrideWindowClosed(row.valid_to, today)) return false;
  return row.status === "active" || row.status === "pending_approval";
}

/**
 * The roles a 403 named. D-263's revoke refusal is the platform's own
 * PERMISSION_DENIED carrying `required_role` as a STRUCTURED extension
 * (D-225: a fact, not a sentence) — and RFC 9457 extensions are spread as
 * top-level members of the problem body, never under an `extensions`
 * envelope (the catalogue's `version-schedule.ts` learned that the hard
 * way). Read defensively: the member is untyped on the wire, so anything but
 * a non-empty array of strings is "the server named no role" and the caller
 * falls back to the server's title.
 */
export function requiredRolesOf(error: unknown): readonly string[] | undefined {
  if (!(error instanceof RequestError) || error.problem === undefined) return undefined;
  const raw = (error.problem as Record<string, unknown>)["required_role"];
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  const roles = raw.filter((role): role is string => typeof role === "string" && role.length > 0);
  return roles.length === raw.length ? roles : undefined;
}

/** True when BOTH price keys are absent — the §7 omission, as opposed to the
 * kind's own `null` on the number it does not carry. */
export function overridePriceRedacted(row: {
  readonly unit_price_minor?: number | null | undefined;
  readonly discount_pct?: string | null | undefined;
}): boolean {
  return row.unit_price_minor === undefined && row.discount_pct === undefined;
}

export type RequiredApprover = "facility_admin" | "group_admin";

export type ProposalOutcome =
  | { readonly kind: "active" }
  | {
      readonly kind: "pending";
      readonly approver: RequiredApprover;
      /** Minor units per base unit short of the guard — present only for a
       * reader with `sales.margin.read` (server-computed, §4.6 "impact"). */
      readonly delta_minor?: number;
      readonly margin_pct?: string;
      readonly guard_pct?: string;
    };

/**
 * What the 201 said, folded for the outcome sentence. `undefined` for a row
 * that is neither — a create never answers `expired`/`revoked`, so reaching
 * one here would be a server the client does not understand, and the caller
 * then says nothing rather than something wrong.
 */
export function proposalOutcome(row: {
  readonly status: string;
  readonly review?:
    | {
        readonly required_approver: RequiredApprover;
        readonly delta_minor?: number | undefined;
        readonly margin_pct?: string | undefined;
        readonly guard_pct?: string | undefined;
      }
    | undefined;
}): ProposalOutcome | undefined {
  if (row.status === "active") return { kind: "active" };
  if (row.status !== "pending_approval") return undefined;
  // A pending row without `review` is a list row read AFTER the fact by a
  // reader the server gave no evidence to; the approver tier is unknown and
  // the safe statement is the stricter one.
  const review = row.review;
  if (review === undefined) return { kind: "pending", approver: "group_admin" };
  return {
    kind: "pending",
    approver: review.required_approver,
    ...(review.delta_minor !== undefined ? { delta_minor: review.delta_minor } : {}),
    ...(review.margin_pct !== undefined ? { margin_pct: review.margin_pct } : {}),
    ...(review.guard_pct !== undefined ? { guard_pct: review.guard_pct } : {}),
  };
}

// ---------------------------------------------------------------------------
// Form validation — the contract's own regexes, mirrored so the refusal is
// said beside the field before the round trip (D-040 two layers; the server
// still validates).
// ---------------------------------------------------------------------------

/** `SalesPct` — numeric(5,2), 0–100, exact decimal text. */
const PCT = /^(100(\.00?)?|\d{1,2}(\.\d{1,2})?)$/u;
/** `SalesQtyOrZero` — numeric(14,3), ≥ 0. */
const QTY_OR_ZERO = /^\d{1,11}(\.\d{1,3})?$/u;

/** Canonical percent text ("30" → "30.00", "7.5" → "7.50") or `undefined`
 * when the text is not a §2.9 percentage. Canonicalised so two proposals of
 * the same discount are the same wire string. */
export function parsePct(text: string): string | undefined {
  const trimmed = text.trim();
  if (!PCT.test(trimmed)) return undefined;
  const [whole, frac = ""] = trimmed.split(".");
  return `${whole}.${frac.padEnd(2, "0")}`;
}

/** Canonical base-UoM quantity text, or `undefined` when not a §2.9
 * `min_qty`. An EMPTY field is the caller's "omit it" (the server defaults
 * to `"0"` — applies at every quantity), so it is handled before this. */
export function parseMinQty(text: string): string | undefined {
  const trimmed = text.trim();
  if (!QTY_OR_ZERO.test(trimmed)) return undefined;
  const [whole, frac = ""] = trimmed.split(".");
  return `${whole}.${frac.padEnd(3, "0")}`;
}

/** §2.9's `customer_price_overrides_validity_ordered` CHECK — an empty end
 * date is open-ended and always ordered. ISO dates compare as text. */
export function validityOrdered(validFrom: string, validTo: string): boolean {
  if (validTo.length === 0) return true;
  return validTo >= validFrom;
}
